import Hero from '@referralexchange/rea-ui-react/src/components/blocks/Hero';
import Image from '@referralexchange/rea-ui-react/src/atoms/Image';
import Content from '@referralexchange/rea-ui-react/src/atoms/Content';
import Heading from '@referralexchange/rea-ui-react/src/atoms/Heading';
import SidebarContainer from '@referralexchange/rea-ui-react/src/components/blocks/SidebarContainer';
import NumberedHeadingContent from '@referralexchange/rea-ui-react/src/components/content/NumberedHeadingContent';
import InfographicList from '@referralexchange/rea-ui-react/src/components/InfographicList';
import ComputerWithMagnifyingGlassIcon from '@referralexchange/rea-ui-react/src/svgs/infographicList/computer-with-magnifying-glass';
import CommunityIcon from '@referralexchange/rea-ui-react/src/svgs/infographicList/community';
import HandsHoldingHouseIcon from '@referralexchange/rea-ui-react/src/svgs/infographicList/hands-holding-house';
import HomepageBrowserIcon from '@referralexchange/rea-ui-react/src/svgs/infographicList/homepage-browser';
import HouseWithYardSignIcon from '@referralexchange/rea-ui-react/src/svgs/infographicList/house-with-yard-sign';
import NewspaperWithHouseIcon from '@referralexchange/rea-ui-react/src/svgs/infographicList/newspaper-with-house';
import styles from '../components/InfoPages/InfoPage.module.scss';

import Head from '~/components/Meta/Head';
import Anchors from '@referralexchange/rea-ui-react/src/components/navigation/Anchors';
import Link from 'next/link';

import MessageAgentCta from '~/components/blocks/MessageAgentCta';
import { HOMES_FOR_SALE_NEAR_YOU_FUNNEL } from '~/config/funnel';
import { useState } from 'react';

export default function Page({}) {
  const sidebar = (
    <Anchors
      items={[
        {
          name: (
            <MessageAgentCta
              standalone
              funnel={HOMES_FOR_SALE_NEAR_YOU_FUNNEL}
              fullInteractive
            />
          ),
          href: null,
          hideOnMobile: true,
        },
        {
          name: 'How to Find Homes for Sale Near You',
          href: '#how-to-find-homes-for-sale-near-you',
        },
      ]}
    />
  );

  const numberedHeadingContent1 = [
    {
      heading: 'Use Listing Websites',
      content: (
        <p>
          There are multiple ways to find homes for sale near you by hopping
          online and using the internet. Many sites have an extensive list of
          homes for sale that include details such as the number of
          bedrooms/bathrooms, sq footage, nearby schools and more. Typically,
          you can look in a specific neighborhood or city, eliminating the areas
          you are not interested in finding homes. You may even be able to sort
          or narrow your search based on property type such as a single family
          home, condo, or townhome for sale. Unfortunately, some details or
          records on these sites may not be entirely accurate or updated,
          however they serve as a great starting point for your real estate
          journey. These days, some of these sites even have the ability to sell
          or help you buy a property through their brokerage services.
        </p>
      ),
    },
    {
      heading: 'Visit Brokerage Websites',
      content: (
        <p>
          In addition to websites that mostly focus on presenting listings, many
          major brokerage websites also feature the homes currently for sale by
          the agents that are a part of that brokerage. As some agents prefer
          not to use the aforementioned listing websites, there may be
          additional homes for sale on a brokerage website that you have not
          previously seen. Additionally, the functionality of the site may be a
          bit different and include other details that aren’t on the first group
          of sites.
        </p>
      ),
    },
    {
      heading: 'Work with a Real Estate Agent',
      content: (
        <p>
          The best, and most effective way to find homes for sale near you is to
          begin working with a real estate agent. A top agent in your area is
          connected to the best local resources for finding homes for sale
          including your local MLS (or multiple listing service, a tool that
          only real estate agents can access). The MLS connects an agent to all
          available homes for sale in the area and includes all the details you
          want to know about a property. Plus, after developing a connection
          with your real estate agent they can prioritize sharing the homes for
          sale that best meet your needs, saving your time and energy from
          viewing homes that are a bad fit. Additionally, agents often know
          about homes that will be available before they’re officially listed
          for sale (known as a pocket listing) through their own business or
          connections with other agents, granting you exclusive access to
          certain properties.
        </p>
      ),
    },
  ];

  const numberedHeadingContent2 = [
    {
      heading: 'Search for Open Houses',
      content: (
        <p>
          Visiting open houses in the neighborhoods, or expanding to the entire
          city in your area, is a great way to find homes for sale near you.
          This is a great way to take the next step from browsing and actually
          view homes for sale to determine your likes, dislikes, and identify
          what options exist in different properties. You’re most likely to find
          open houses hosted on the weekend, and they tend to be available
          earlier in the listing’s lifecycle. While you can definitely find open
          houses by driving around a given neighborhood (most agents will market
          them heavily with signs) you can also find them by checking message
          boards, city resource pages, and using social media. You can even take
          this a step further and follow an agent you like on social media to
          discover open houses and new listings as soon as they’re announced.
        </p>
      ),
    },
    {
      heading: 'Connect with the Community',
      content: (
        <p>
          Another great way to find homes for sale near you is by plugging into
          your local community and asking around about homes for sale or that
          will soon be listed on the market. Word of mouth is still an
          incredibly effective way to learn about happenings in some
          neighborhoods, especially if the residents are especially active
          online. Connecting with neighbors, or through social networks such as
          schools, gyms, or churches, are great way to discover what’s happening
          in the residential real estate market.
        </p>
      ),
    },
    {
      heading: 'Subscribe to Local Periodicals',
      content: (
        <p>
          Similar to some websites, you can find brief listings of homes for
          sale in local newspapers and magazines. Oftentimes these are submitted
          by a real estate agent to market a listing, so they’ll include short
          descriptions including 1-2 photos of the property, square footage, and
          the number of beds/bathrooms. These brief listings are meant to act as
          a launching point to initiate contact and learn more about the
          property, so either you or your agent can reach out using the included
          contact info on the listing.
        </p>
      ),
    },
  ];

  const infographicList = {
    items: [
      {
        icon: (
          <ComputerWithMagnifyingGlassIcon title="Use Listing Websites logo" />
        ),
        text: 'Use Listing Websites',
      },
      {
        icon: <HomepageBrowserIcon title="Visit Brokerage Websites logo" />,
        text: 'Visit Brokerage Websites',
      },
      {
        icon: (
          <HandsHoldingHouseIcon title="Work with a Real Estate Agent logo" />
        ),
        text: 'Work with a Real Estate Agent',
      },
      {
        icon: <HouseWithYardSignIcon title="Search for Open Houses logo" />,
        text: 'Search for Open Houses',
      },
      {
        icon: <CommunityIcon title="Connect with the Community logo" />,
        text: 'Connect with the Community',
      },
      {
        icon: (
          <NewspaperWithHouseIcon title="Subscribe to Local Periodicals logo" />
        ),
        text: 'Subscribe to Local Periodicals',
      },
    ],
    title: 'How to Find Homes for Sale Near You',
  };

  return (
    <>
      <Head
        url={`${process.env.CLIENT_ORIGIN}/how-to-compare-real-estate-agents`}
        title={'How to Find Homes for Sale Near You | RealEstateAgents.com'}
        description={
          "It's hard to find a property that checks all the boxes once you're ready to buy. Use this guide as a resource for finding homes for sale near you."
        }
      />
      <Hero
        heading={'How to Find Homes for Sale Near You'}
        content={
          'A major step in buying a home is finding one you actually want to purchase! Learn how below.'
        }
        image={
          <Image
            src="/static/images/how-to-find-homes-for-sale-near-you/hero.png"
            priority
            alt={'How to Find Homes For Sale Near You'}
          />
        }
      />
      <SidebarContainer
        sidebar={sidebar}
        mobileMenuProps={{
          top: true,
          defaultTitle: 'How to Find Homes for Sale Near You',
        }}
        mobileMenuEnabled={true}
      >
        <Content>
          <Heading
            tag="h2"
            type="h1"
            anchor={'How to Find Homes for Sale Near You'}
          >
            Finding Homes for Sale
          </Heading>
          <p>
            With its constant ups and downs the housing market can be incredibly
            difficult to navigate. As a buyer it seems that home prices are
            always either rising or falling, with more and more homes moving off
            the market with every minute you spend searching. This guide will
            point you in the right direction of how to find houses for sale near
            you by using some readily available resources!
          </p>
          <InfographicList
            items={infographicList.items}
            title={infographicList.title}
          />
          <NumberedHeadingContent items={numberedHeadingContent1} />
          <MessageAgentCta standalone funnel={HOMES_FOR_SALE_NEAR_YOU_FUNNEL} />
          <NumberedHeadingContent items={numberedHeadingContent2} offset={3} />
          <p>
            Whether you’re ready to begin submitting offers or just curious what
            homes are available to buy, the tips listed here will guide you to
            find homes for sale. If you’re not working with an agent or{' '}
            <Link href={'/how-to-find-a-real-estate-agent'}>
              still attempting to find one
            </Link>{' '}
            it’s even more difficult to access home listings (another word for
            homes for sale) as they receive exclusive access to resources that
            feature homes for sale, so we recommend connecting with a top real
            estate agent today.
          </p>
          <MessageAgentCta standalone funnel={HOMES_FOR_SALE_NEAR_YOU_FUNNEL} />
        </Content>
      </SidebarContainer>
      <div className={styles['InfoPage__mobile-cta-sticky']}>
        <MessageAgentCta
          standalone
          funnel={HOMES_FOR_SALE_NEAR_YOU_FUNNEL}
          fullInteractive
        />
      </div>
    </>
  );
}
